import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { Brand } from "@/components/common/Brand";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/auth-context";
import { NotificationBell } from "@/components/common/NotificationBell";
import { cn } from "@/lib/utils";

function NavLinks({ items, onNavigate }) {
  const { pathname } = useLocation();
  return (
    <nav className="flex flex-col gap-1">
      {items.map((it) => {
        const active = it.end ? pathname === it.to : pathname.startsWith(it.to);
        return (
          <Link
            key={it.to}
            to={it.to}
            onClick={onNavigate}
            data-testid={`nav-${it.key}`}
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors",
              active ? "bg-primary text-primary-foreground" : "text-white/70 hover:bg-white/10 hover:text-white",
            )}
          >
            <it.icon className="h-[18px] w-[18px]" />
            {it.label}
          </Link>
        );
      })}
    </nav>
  );
}

function SidebarInner({ items, roleLabel, onNavigate }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col bg-secondary text-white">
      <div className="stripe-green h-3 w-full shrink-0" />
      <div className="flex flex-1 flex-col p-4">
        <div className="px-2 pb-6 pt-2">
          <Brand tone="inverse" />
          <span className="mt-3 inline-block rounded-full bg-popyellow px-3 py-1 font-display text-[11px] uppercase tracking-widest text-foreground">
            {roleLabel}
          </span>
        </div>
        <NavLinks items={items} onNavigate={onNavigate} />
        <div className="mt-auto border-t border-white/15 pt-4">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                {(user?.name || "?").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm">{user?.name}</p>
              <p className="truncate text-xs text-white/50">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="mt-1 w-full justify-start gap-3 rounded-xl text-white/70 hover:bg-white/10 hover:text-white"
            onClick={() => signOut().then(() => navigate("/login"))}
            data-testid="sign-out-button"
          >
            <LogOut className="h-[18px] w-[18px]" /> Sign out
          </Button>
        </div>
      </div>
      <div className="checker checker-red checker-sm h-8 w-full shrink-0" />
    </div>
  );
}

export function StaffShell({ items, roleLabel, title, children, headerRight }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[264px_1fr]">
      <aside className="sticky top-0 hidden h-screen lg:block">
        <SidebarInner items={items} roleLabel={roleLabel} />
      </aside>
      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b bg-background/80 px-4 py-3 backdrop-blur lg:px-8">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 border-0 p-0">
              <SidebarInner items={items} roleLabel={roleLabel} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
          <h1 className="font-display text-lg lg:text-xl">{title}</h1>
          <div className="ml-auto flex items-center gap-2">
            {headerRight}
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
